import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  List,
  ListItemButton,
  Typography,
  alpha,
  useTheme,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useTeamPresenceOptional } from "../context/TeamPresenceContext";
import TeamPresenceAvatar from "./TeamPresenceAvatar";
import SidebarSection from "./TeamChat/SidebarSection";
import { ensureNamespaceLoaded } from "../i18n/lazyNamespaces";

const COLLAPSE_STORAGE_KEY = "xproTeamPresenceRosterCollapsed";

function loadCollapsed() {
  try {
    return localStorage.getItem(COLLAPSE_STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

/**
 * Lista da equipe agrupada por presença (online → ausente → offline).
 * Clique em um usuário chama onSelectUser (abrir DM existente ou criar nova).
 */
export default function TeamPresenceRoster({
  compact = false,
  inheritScroll = false,
  currentUserId,
  onSelectUser,
}) {
  const { t: tTeam } = useTranslation("team");
  const { t: tLive } = useTranslation("settings");
  const theme = useTheme();
  const presence = useTeamPresenceOptional();
  const [collapsed, setCollapsed] = useState(loadCollapsed);
  const [openingUserId, setOpeningUserId] = useState(null);

  useEffect(() => {
    void ensureNamespaceLoaded("team");
  }, []);

  const toggle = useCallback(() => {
    setCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(COLLAPSE_STORAGE_KEY, next ? "1" : "0");
      } catch {
        /* ignore */
      }
      return next;
    });
  }, []);

  const handleUserClick = useCallback(
    async (user) => {
      if (!onSelectUser || !user?.id) return;
      const uid = Number(user.id);
      if (!Number.isFinite(uid) || uid <= 0) return;
      if (currentUserId != null && uid === Number(currentUserId)) return;
      if (openingUserId != null) return;

      setOpeningUserId(uid);
      try {
        await onSelectUser(user);
      } finally {
        setOpeningUserId(null);
      }
    },
    [onSelectUser, currentUserId, openingUserId]
  );

  if (!presence?.operators?.length) return null;

  const { online, away, offline } = presence.grouped;
  const total = online.length + away.length + offline.length;
  if (!total) return null;

  const youLabel = tTeam("you", { defaultValue: "Você" });
  const clickable = typeof onSelectUser === "function";

  const renderGroup = (title, users) => {
    if (!users?.length) return null;
    return (
      <Box sx={{ mb: compact ? 0.5 : 1 }}>
        <Typography
          variant="caption"
          sx={{
            px: 2,
            py: 0.5,
            display: "block",
            fontWeight: 700,
            color: "text.secondary",
            textTransform: "uppercase",
            letterSpacing: "0.05em",
            fontSize: "0.65rem",
          }}
        >
          {title} · {users.length}
        </Typography>
        <List dense disablePadding>
          {users.map((u) => {
            const uid = Number(u.id);
            const isSelf = currentUserId != null && uid === Number(currentUserId);
            const isOpening = openingUserId === uid;
            const canClick = clickable && !isSelf && openingUserId == null;

            const row = (
              <ListItemButton
                key={u.id}
                dense
                disabled={!canClick && (isSelf || isOpening)}
                onClick={canClick ? () => handleUserClick(u) : undefined}
                sx={{
                  py: 0.5,
                  px: 2,
                  color: "text.primary",
                  opacity: 1,
                  cursor: canClick ? "pointer" : isSelf ? "default" : "wait",
                  "&.Mui-disabled": { opacity: isSelf ? 0.72 : 1 },
                  ...(canClick
                    ? {
                        "&:hover": { bgcolor: "action.hover" },
                      }
                    : {}),
                }}
              >
                <TeamPresenceAvatar
                  userId={u.id}
                  src={u.imagem}
                  name={u.nome}
                  size={compact ? 28 : 32}
                  dotSize={9}
                />
                <Typography
                  variant="body2"
                  color="text.primary"
                  noWrap
                  sx={{ ml: 1.25, flex: 1, fontWeight: isSelf ? 700 : 500 }}
                >
                  {u.nome}
                  {isSelf ? ` (${youLabel})` : ""}
                </Typography>
                {isOpening ? (
                  <CircularProgress size={16} sx={{ ml: 0.5, flexShrink: 0 }} />
                ) : null}
              </ListItemButton>
            );

            if (!canClick) return row;

            return (
              <Tooltip
                key={u.id}
                title={tTeam("presenceOpenDm", { defaultValue: "Abrir conversa" })}
                placement="right"
                enterDelay={400}
              >
                {row}
              </Tooltip>
            );
          })}
        </List>
      </Box>
    );
  };

  return (
    <Box
      sx={{
        borderTop: 1,
        borderColor: "divider",
        bgcolor: alpha(theme.palette.primary.main, 0.02),
        flexShrink: 0,
      }}
    >
      <SidebarSection
        title={tTeam("teamPresenceTitle", { defaultValue: "Equipe agora" })}
        count={total}
        collapsed={collapsed}
        onToggleCollapse={toggle}
      >
        <Box
          sx={
            inheritScroll
              ? undefined
              : {
                  maxHeight: compact ? 200 : 280,
                  overflow: "auto",
                }
          }
          className={inheritScroll ? undefined : "sidebar-scroll"}
        >
          {renderGroup(tLive("dashboardPage.live.onlineNow"), online)}
          {renderGroup(tLive("dashboardPage.live.away"), away)}
          {renderGroup(tLive("dashboardPage.live.presenceOffline"), offline)}
        </Box>
      </SidebarSection>
    </Box>
  );
}
